import { useMemo, useState, type ReactNode } from "react";
import { CanAccess, useGetIdentity, useGetList, useTranslate } from "ra-core";
import { Navigate } from "react-router";
import { Label } from "@/components/ui/label";

import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { getCurrentMonthPeriod, periodMonthKey } from "../incentives/periods";
import type { Actor } from "../providers/commons/canAccess";
import { capturedInMonth } from "../opciones/inventoryStats";
import { useMonthCaptures } from "../opciones/useMonthCaptures";
import type {
  AdvisorGoal,
  AdvisorGoalScope,
  AppRole,
  BusinessArm,
  InventoryItem,
  LiderTeamMember,
  Sale,
} from "../types";
import {
  getAdvisorGoalAudience,
  getTeamGoalAudience,
  sortGoalAudience,
  type GoalAudienceMember,
} from "./goalsAudience";
import { GoalRowInput } from "./GoalRowInput";

// Generous, fixed caps (mirrors `useAdvisorProgress.ts`'s per-query cap
// convention): a single page bounded regardless of team size, never one
// request per advisor -- the ticket's `performance` non-functional
// requirement (40 advisors under 1s with a single goals query).
const MAX_AUDIENCE_SALES = 500;

/**
 * Almikia Epic E7 (TASK-091): the "Metas" screen. Reachable from the desktop
 * menu only (ASSUMPTION, like Clientes) -- registered as `/metas` in
 * `CRM.tsx`'s desktop `<CustomRoutes>`. Gated by `canAccess("advisor_goals",
 * "list")` (TASK-088, mirroring TASK-087's RLS): direccion, lead_manager and
 * lider reach it; asesora and every back-office role are redirected home.
 */
export const GoalsPage = () => (
  <CanAccess
    resource="advisor_goals"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <GoalsPageContent />
  </CanAccess>
);

GoalsPage.path = "/metas";

const PageShell = ({
  monthValue,
  onMonthChange,
  children,
}: {
  monthValue: string;
  onMonthChange: (value: string) => void;
  children: ReactNode;
}) => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-6 p-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-2xl font-bold">{translate("crm.goals.title")}</h1>
        <div className="flex flex-col gap-1">
          <Label htmlFor="goals-month">
            {translate("crm.goals.month_label")}
          </Label>
          <input
            id="goals-month"
            type="month"
            value={monthValue}
            onChange={(event) => onMonthChange(event.target.value)}
            className="h-11 rounded-md border border-input bg-transparent px-3 text-sm tabular-nums shadow-xs"
          />
        </div>
      </div>
      {children}
    </div>
  );
};

const GoalSection = ({
  title,
  members,
  scope,
  goalsByKey,
  monthKey,
  onSaved,
  captureOptions,
}: {
  title: string;
  members: GoalAudienceMember[];
  scope: AdvisorGoalScope;
  goalsByKey: Map<string, AdvisorGoal>;
  monthKey: string;
  onSaved: () => void;
  /** The month's options when the count is complete; undefined hides each advisor's figure. */
  captureOptions?: InventoryItem[];
}) => (
  <div className="flex flex-col gap-3">
    <h2 className="text-xl font-semibold text-muted-foreground">{title}</h2>
    <div className="flex flex-col gap-3">
      {members.map((member) => (
        <GoalRowInput
          key={member.id}
          member={member}
          scope={scope}
          goal={goalsByKey.get(`${member.id}-${scope}`)}
          monthKey={monthKey}
          onSaved={onSaved}
          capturedCount={
            scope === "advisor" && captureOptions
              ? capturedInMonth(captureOptions, member.id, monthKey)
              : undefined
          }
        />
      ))}
    </div>
  </div>
);

const GoalsPageContent = () => {
  const translate = useTranslate();
  const { identity, isPending: isIdentityPending } = useGetIdentity();
  const now = useMemo(() => new Date(), []);
  const [monthValue, setMonthValue] = useState(() =>
    periodMonthKey(getCurrentMonthPeriod(now)).slice(0, 7),
  );
  const monthKey = `${monthValue}-01`;

  const actor: Actor | undefined = identity
    ? {
        role: identity.role as AppRole,
        id: identity.id,
        business_arm: (identity.business_arm ?? null) as BusinessArm | null,
      }
    : undefined;

  const isLider = actor?.role === "lider";
  const canSeeTeamGoals =
    actor?.role === "direccion" || actor?.role === "lead_manager";

  const salesResult = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: MAX_AUDIENCE_SALES },
      sort: { field: "first_name", order: "ASC" },
      filter: { "role@in": "(asesora,lider)" },
    },
    { enabled: !!actor && !isLider },
  );

  const teamMembersResult = useGetList<LiderTeamMember>(
    "lider_team_members",
    { pagination: { page: 1, perPage: MAX_AUDIENCE_SALES } },
    { enabled: !!actor && isLider },
  );

  const advisorAudience = useMemo<GoalAudienceMember[]>(() => {
    if (!actor) return [];
    if (isLider) {
      return sortGoalAudience(
        (teamMembersResult.data ?? []).map((member) => ({
          id: member.id,
          first_name: member.first_name,
          last_name: member.last_name,
          advisor_code: member.advisor_code,
        })),
      );
    }
    return getAdvisorGoalAudience(actor, salesResult.data ?? []);
  }, [actor, isLider, teamMembersResult.data, salesResult.data]);

  const teamAudience = useMemo<GoalAudienceMember[]>(
    () =>
      actor && canSeeTeamGoals
        ? getTeamGoalAudience(actor, salesResult.data ?? [])
        : [],
    [actor, canSeeTeamGoals, salesResult.data],
  );

  const audienceIds = useMemo(
    () =>
      Array.from(
        new Set([...advisorAudience, ...teamAudience].map((m) => m.id)),
      ),
    [advisorAudience, teamAudience],
  );
  const hasAudience = audienceIds.length > 0;

  const goalsResult = useGetList<AdvisorGoal>(
    "advisor_goals",
    {
      pagination: { page: 1, perPage: Math.max(audienceIds.length * 2, 1) },
      filter: {
        period_month: monthKey,
        "sales_id@in": `(${audienceIds.join(",")})`,
      },
    },
    { enabled: hasAudience },
  );

  const goalsByKey = useMemo(() => {
    const map = new Map<string, AdvisorGoal>();
    for (const goal of goalsResult.data ?? []) {
      map.set(`${goal.sales_id}-${goal.scope}`, goal);
    }
    return map;
  }, [goalsResult.data]);

  const monthCaptures = useMonthCaptures(monthKey, hasAudience);
  const captureOptions =
    monthCaptures.isTruncated || monthCaptures.error
      ? undefined
      : monthCaptures.options;

  const refetchAll = () => {
    salesResult.refetch();
    teamMembersResult.refetch();
    goalsResult.refetch();
  };

  const isPending =
    isIdentityPending ||
    (!isLider && salesResult.isPending) ||
    (isLider && teamMembersResult.isPending) ||
    (hasAudience && goalsResult.isPending);
  const error =
    salesResult.error ?? teamMembersResult.error ?? goalsResult.error;

  return (
    <PageShell monthValue={monthValue} onMonthChange={setMonthValue}>
      {isPending ? (
        <ListRowsSkeleton rows={5} columns={3} />
      ) : error ? (
        <ListErrorState
          message={translate("crm.common.list_error")}
          onRetry={refetchAll}
        />
      ) : advisorAudience.length === 0 && teamAudience.length === 0 ? (
        <ListEmptyState
          title={translate("crm.goals.empty_title")}
          description={translate("crm.goals.empty_description")}
        />
      ) : (
        <div className="flex flex-col gap-6">
          <GoalSection
            title={translate("crm.goals.section_advisor")}
            members={advisorAudience}
            scope="advisor"
            goalsByKey={goalsByKey}
            monthKey={monthKey}
            onSaved={refetchAll}
            captureOptions={captureOptions}
          />
          {canSeeTeamGoals && teamAudience.length > 0 ? (
            <GoalSection
              title={translate("crm.goals.section_team")}
              members={teamAudience}
              scope="team"
              goalsByKey={goalsByKey}
              monthKey={monthKey}
              onSaved={refetchAll}
            />
          ) : null}
        </div>
      )}
    </PageShell>
  );
};
